const DB_NAME = 'easy-editor-queue'
const STORE_NAME = 'pending_edits'
const DB_VERSION = 1
const MAX_RETRIES = 3

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
      }
    }
    request.onsuccess = (event) => resolve(event.target.result)
    request.onerror = (event) => {
      dbPromise = null
      reject(event.target.error)
    }
  })
  return dbPromise
}

// payload must be a JSON string (e.g. the output of createJsonBody()); syncAll sends it
// verbatim as the fetch body with Content-Type: application/json.
export async function enqueue(provider, observationId, payload, recordParam = 'id') {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const request = store.add({
      provider,
      observationId,
      recordParam: recordParam === 'guid' ? 'guid' : 'id',
      payload: typeof payload === 'string' ? payload : JSON.stringify(payload),
      timestamp: Date.now(),
    })
    request.onsuccess = (event) => resolve(event.target.result)
    tx.onerror = (event) => reject(event.target.error)
    tx.onabort = (event) => reject(event.target.error)
  })
}

export async function getAll() {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const request = store.getAll()
    request.onsuccess = (event) => resolve(event.target.result)
    tx.onerror = (event) => reject(event.target.error)
    tx.onabort = (event) => reject(event.target.error)
  })
}

export async function remove(id) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    store.delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = (event) => reject(event.target.error)
    tx.onabort = (event) => reject(event.target.error)
  })
}

export async function syncAll(provider, sessionId) {
  const all = await getAll()
  const items = all.filter((item) => item.provider === provider)
  if (items.length === 0) return { synced: [], failed: [] }

  const synced = []
  const failed = []

  for (const item of items) {
    const recordParam = item.recordParam === 'guid' ? 'guid' : 'id'
    const recordQuery = item.provider === 'inaturalist'
      ? `state=${encodeURIComponent(item.observationId)}`
      : `${recordParam}=${encodeURIComponent(item.observationId)}`
    const url = `/api/${item.provider}/update?session=${encodeURIComponent(sessionId)}&${recordQuery}`
    let lastError = null
    let success = false

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: item.payload,
        })
        if (response.status === 401) {
          return { authRequired: true, synced, failed }
        }
        if (response.ok && response.status !== 207) {
          await remove(item.id)
          synced.push(item.id)
          success = true
          break
        } else {
          const text = await response.text()
          lastError = `HTTP ${response.status}: ${text}`
        }
      } catch (err) {
        lastError = err.message
      }
    }

    if (!success) {
      failed.push({ id: item.id, error: lastError })
    }
  }

  return { synced, failed }
}

export function _resetDbForTests() {
  dbPromise = null
}
